import React, { useState, useEffect } from 'react';
import { 
  Gift, Crown, Sparkles, Car, Shield, Send, 
  Search, RefreshCw, AlertCircle, CheckCircle2, Clock, Globe
} from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerStore = () => {
  const [activeTab, setActiveTab] = useState('items'); // 'items', 'gifts', 'vip'
  const [gifts, setGifts] = useState([]);
  const [storeItems, setStoreItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Assignment Modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignType, setAssignType] = useState('ITEM'); // 'ITEM' or 'VIP'
  const [selectedItem, setSelectedItem] = useState(null);
  const [targetUserId, setTargetUserId] = useState('');
  const [durationDays, setDurationDays] = useState(30);
  const [vipTier, setVipTier] = useState('VIP'); // 'VIP' or 'SVIP'
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [giftsRes, itemsRes] = await Promise.all([
        managerAPI.getGifts(),
        managerAPI.getStoreItems()
      ]);
      if (giftsRes.data?.success) setGifts(giftsRes.data.gifts || []);
      if (itemsRes.data?.success) setStoreItems(itemsRes.data.items || []);
    } catch (err) {
      console.error("Failed to load store catalog:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAssignItemSubmit = async (e) => {
    e.preventDefault();
    if (!targetUserId.trim()) {
      alert("Please provide User ID, XOXO ID, or User Number.");
      return;
    }

    try {
      setAssignLoading(true);
      if (assignType === 'VIP') {
        const res = await managerAPI.assignVip({
          user_id: targetUserId.trim(),
          vip_tier: vipTier,
          duration_days: durationDays
        });
        alert(res.data?.message || "VIP Tier assigned successfully.");
      } else {
        const res = await managerAPI.assignItem({
          user_id: targetUserId.trim(),
          item_id: selectedItem?.id,
          item_name: selectedItem?.item_name || 'Special Frame',
          item_type: selectedItem?.category || 'FRAME',
          duration_days: durationDays
        });
        alert(res.data?.message || "Item assigned successfully.");
      }
      setShowAssignModal(false);
      setTargetUserId('');
      setSelectedItem(null);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to assign item. Verify that user belongs to your assigned country.");
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/5">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Items, Gifts & VIP Center</h1>
            <span className="px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-bold text-xs uppercase">
              Catalogue & Rewards
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Browse live gifts (View-Only) and assign specialized frames, luxury vehicles, or VIP privileges to country users.
          </p>
        </div>

        {/* Quick Assign VIP Button */}
        <button
          onClick={() => {
            setAssignType('VIP');
            setShowAssignModal(true);
          }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg shadow-amber-500/20"
        >
          <Crown size={16} />
          <span>Assign VIP / SVIP</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-3 border-b border-white/10 pb-4">
        <button
          onClick={() => setActiveTab('items')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'items'
              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
              : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
          }`}
        >
          <Car size={16} />
          <span>Frames & Vehicles ({storeItems.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('gifts')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'gifts'
              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
              : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
          }`}
        >
          <Gift size={16} />
          <span>Gifts Catalog (View Only - {gifts.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('vip')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'vip'
              ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30'
              : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
          }`}
        >
          <Crown size={16} />
          <span>VIP & SVIP Management</span>
        </button>
      </div>

      {/* TAB 1: STORE ITEMS (Frames, Vehicles) */}
      {activeTab === 'items' && (
        <div className="space-y-6">
          <div className="p-4 rounded-2xl bg-indigo-500/5 border border-indigo-500/15 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles size={20} className="text-indigo-400" />
              <div>
                <h4 className="text-sm font-bold text-white">Item Assignment Authority</h4>
                <p className="text-xs text-slate-400">Managers can reward active users in their assigned country with frames or entry vehicles.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {storeItems.map((item) => (
              <div 
                key={item.id} 
                className="bg-[#0b0f19] border border-white/5 rounded-2xl p-4 flex flex-col justify-between hover:border-indigo-500/30 transition-all group"
              >
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto rounded-2xl bg-white/[0.02] border border-white/10 flex items-center justify-center p-2 mb-3">
                    <img 
                      src={item.thumbnail_url || item.preview_url || 'https://placehold.co/100x100?text=Item'} 
                      alt={item.item_name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <h4 className="font-bold text-white text-sm line-clamp-1">{item.item_name}</h4>
                  <span className="inline-block px-2 py-0.5 mt-1 rounded text-[10px] font-semibold bg-white/5 text-indigo-300 uppercase">
                    {item.category || 'ITEM'}
                  </span>
                </div>

                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                  <span className="text-xs font-mono text-amber-400">{item.price || 0} 🪙</span>
                  <button
                    onClick={() => {
                      setSelectedItem(item);
                      setAssignType('ITEM');
                      setShowAssignModal(true);
                    }}
                    className="px-3 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition-all cursor-pointer"
                  >
                    Assign
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: GIFTS (View Only) */}
      {activeTab === 'gifts' && (
        <div className="space-y-6">
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center justify-between text-xs text-slate-400">
            <span>Notice: As an Executive Manager, gift catalogue creation and price alterations are locked by Super Admin. You have full visibility into live gifts.</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {gifts.map((gift) => (
              <div key={gift.id} className="bg-[#0b0f19] border border-white/5 rounded-2xl p-4 text-center">
                <div className="w-16 h-16 mx-auto rounded-xl bg-white/[0.02] border border-white/10 flex items-center justify-center p-2 mb-2">
                  <img 
                    src={gift.gift_thumbnail || 'https://placehold.co/80x80?text=Gift'} 
                    alt={gift.gift_name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <h4 className="font-bold text-white text-xs line-clamp-1">{gift.gift_name}</h4>
                <div className="mt-1 text-xs font-mono font-bold text-amber-400">
                  {Number(gift.price || 0).toLocaleString()} 🪙
                </div>
                <div className="mt-1 text-[10px] text-slate-500 uppercase">{gift.gift_category || 'General'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: VIP / SVIP Management */}
      {activeTab === 'vip' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* VIP Card */}
          <div className="p-8 rounded-3xl bg-gradient-to-br from-indigo-950/40 via-purple-950/30 to-[#0b0f19] border border-indigo-500/20 relative overflow-hidden flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 mb-6">
                <Crown size={28} />
              </div>
              <h3 className="text-2xl font-black text-white mb-2">VIP Membership</h3>
              <p className="text-slate-400 text-sm mb-6">
                Assign VIP status to deserving users in your assigned country. Grants VIP badge, room entry spotlight, and specialized chat bubble.
              </p>

              <ul className="space-y-2 text-xs text-slate-300 mb-8">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-indigo-400" /> Exclusive VIP Profile Ring
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-indigo-400" /> Priority in live audio queue
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-indigo-400" /> Audited assignment logged to Manager ID
                </li>
              </ul>
            </div>

            <button
              onClick={() => {
                setVipTier('VIP');
                setAssignType('VIP');
                setShowAssignModal(true);
              }}
              className="w-full py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm transition-all cursor-pointer shadow-lg shadow-indigo-600/30"
            >
              Assign VIP to User
            </button>
          </div>

          {/* SVIP Card */}
          <div className="p-8 rounded-3xl bg-gradient-to-br from-amber-950/40 via-yellow-950/30 to-[#0b0f19] border border-amber-500/30 relative overflow-hidden flex flex-col justify-between">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-6">
                <Sparkles size={28} />
              </div>
              <h3 className="text-2xl font-black text-white mb-2">Super VIP (SVIP)</h3>
              <p className="text-slate-400 text-sm mb-6">
                Elite status reserved for high-value network contributors and agency leadership. Grants animated supercar entrance and gold spotlight.
              </p>

              <ul className="space-y-2 text-xs text-slate-300 mb-8">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-amber-400" /> Animated Golden SVIP Avatar Frame
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-amber-400" /> Full-screen Supercar Entrance effect
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-amber-400" /> Highest room privilege level
                </li>
              </ul>
            </div>

            <button
              onClick={() => {
                setVipTier('SVIP');
                setAssignType('VIP');
                setShowAssignModal(true);
              }}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-sm transition-all cursor-pointer shadow-lg shadow-amber-500/20"
            >
              Assign SVIP to User
            </button>
          </div>
        </div>
      )}

      {/* ASSIGNMENT MODAL */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f1026] border border-indigo-500/30 rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <div className="flex items-center gap-3 text-indigo-400 mb-4">
              {assignType === 'VIP' ? <Crown size={28} /> : <Sparkles size={28} />}
              <h3 className="text-xl font-black text-white">
                {assignType === 'VIP' ? `Assign ${vipTier}` : `Assign ${selectedItem?.item_name || 'Item'}`}
              </h3>
            </div>

            <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20 mb-6 text-xs text-indigo-200">
              Assigned directly to user within your country. Activity logged to Executive Audit Record.
            </div>

            <form onSubmit={handleAssignItemSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Target User ID / XOXO ID *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 10024 or xoxo_9831"
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  className="w-full bg-[#030712] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors font-mono"
                />
              </div>

              {assignType === 'VIP' && (
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                    Tier Select
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setVipTier('VIP')}
                      className={`py-2 rounded-xl text-xs font-bold transition-all ${
                        vipTier === 'VIP' ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400'
                      }`}
                    >
                      VIP
                    </button>
                    <button
                      type="button"
                      onClick={() => setVipTier('SVIP')}
                      className={`py-2 rounded-xl text-xs font-bold transition-all ${
                        vipTier === 'SVIP' ? 'bg-amber-600 text-white' : 'bg-white/5 text-slate-400'
                      }`}
                    >
                      SVIP
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Duration (Days)
                </label>
                <select
                  value={durationDays}
                  onChange={(e) => setDurationDays(Number(e.target.value))}
                  className="w-full bg-[#030712] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
                >
                  <option value={7}>7 Days (1 Week)</option>
                  <option value={15}>15 Days</option>
                  <option value={30}>30 Days (1 Month)</option>
                  <option value={90}>90 Days (3 Months)</option>
                  <option value={365}>365 Days (1 Year)</option>
                </select>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-semibold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assignLoading}
                  className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold transition-colors cursor-pointer"
                >
                  {assignLoading ? 'Assigning...' : 'Confirm Assignment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerStore;
